// One error type for every failed call (FR-033 to FR-035, FR-040, FR-041; data-model §6).
// Built only from the HTTP status, the response headers X-Request-Id / Retry-After and the problem
// body's code, requestId, retryAfterSeconds and errors[]. It never holds the URL, query, request
// headers or request body. A rejected browser origin surfaces as `offline` (the browser hides it).
import type { Api } from "./types";

export type AppErrorKind =
  | "offline"
  | "timeout"
  | "unauthenticated"
  | "forbidden"
  | "notFound"
  | "validation"
  | "rateLimited"
  | "unavailable"
  | "server"
  | "conflict"
  | "unknown";

export type AppError = {
  readonly kind: AppErrorKind;
  /** HTTP status; null without a response. */
  readonly status: number | null;
  /** ProblemDetail.code, which may be unknown to this client. */
  readonly code: string | null;
  /** ProblemDetail.requestId, else the X-Request-Id header. */
  readonly requestId: string | null;
  readonly fieldErrors: readonly Api["FieldError"][];
  /** ProblemDetail.retryAfterSeconds first, else the Retry-After header (429, 503). */
  readonly retryAfterSeconds: number | null;
  /** 002: the clients the account can use, only from a NOT_PERMITTED_FOR_CLIENT refusal; else null. */
  readonly allowedClients: readonly Api["Client"][] | null;
  /** 006: only from a CLASS_IN_USE refusal (ClassListRefusedProblem). */
  readonly classesInUse?: readonly Api["ClassSectionRef"][];
  /** 006: only from a VERSION_CONFLICT refusal (ClassListRefusedProblem). */
  readonly currentClasses?: Api["SchoolClassList"];
  /** 012: only from an EXPORT_TOO_LARGE refusal (OrderProblem). */
  readonly matchCount?: number;
};

const CLIENTS: readonly Api["Client"][] = ["app", "admin", "driver"];

export type HttpFailure = {
  response: { status: number; headers: Headers };
  /** The parsed response body (a problem object, text, or undefined). */
  body: unknown;
};

export type NetworkFailure = {
  thrown: unknown;
  /** True when the call's own timeout aborted the request. */
  timedOut: boolean;
};

const KIND_BY_CODE: Readonly<Record<string, AppErrorKind>> = {
  UNAUTHENTICATED: "unauthenticated",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "notFound",
  VALIDATION_FAILED: "validation",
  MALFORMED_REQUEST: "validation",
  RATE_LIMITED: "rateLimited",
  // 002 (data-model §11; R11)
  INVALID_CREDENTIALS: "unauthenticated",
  SESSION_ENDED: "unauthenticated",
  EMAIL_NOT_VERIFIED: "forbidden",
  ACCOUNT_DISABLED: "forbidden",
  NOT_PERMITTED_FOR_CLIENT: "forbidden",
  TOKEN_INVALID_OR_EXPIRED: "validation",
  SERVICE_UNAVAILABLE: "unavailable",
  INTERNAL_ERROR: "server",
  // 006 (contracts/consumes.md)
  CLASS_IN_USE: "conflict",
  VERSION_CONFLICT: "conflict",
  // 004 (research R15)
  SCHOOL_CODE_INVALID: "validation",
  SCHOOL_NOT_ACTIVE: "conflict",
  STUDENT_HAS_OPEN_ORDER: "conflict",
  STUDENT_NOT_LINKED: "conflict",
  DUPLICATE_STUDENT: "conflict",
  STUDENT_LIMIT_REACHED: "conflict",
  ADDRESS_LIMIT_REACHED: "conflict",
};

function kindByStatus(status: number): AppErrorKind {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 409) return "conflict";
  if (status === 400 || status === 422) return "validation";
  if (status === 429) return "rateLimited";
  if (status === 503) return "unavailable";
  if (status >= 500) return "server";
  return "unknown";
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const nonEmptyString = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;

function seconds(v: unknown): number | null {
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return v;
  if (typeof v === "string" && /^\d+$/.test(v.trim())) return Number(v.trim());
  return null;
}

const isNetworkFailure = (
  f: HttpFailure | NetworkFailure,
): f is NetworkFailure => "thrown" in f;

function isAbort(thrown: unknown): boolean {
  if (typeof thrown !== "object" || thrown === null) return false;
  const name = (thrown as { name?: unknown }).name;
  return name === "AbortError" || name === "TimeoutError";
}

export function toAppError(failure: HttpFailure | NetworkFailure): AppError {
  if (isNetworkFailure(failure)) {
    return {
      kind: failure.timedOut || isAbort(failure.thrown) ? "timeout" : "offline",
      status: null,
      code: null,
      requestId: null,
      fieldErrors: [],
      retryAfterSeconds: null,
      allowedClients: null,
    };
  }

  const { status, headers } = failure.response;
  const body = isRecord(failure.body) ? failure.body : {};
  const code = nonEmptyString(body.code);
  const kind =
    (code !== null ? KIND_BY_CODE[code] : undefined) ?? kindByStatus(status);

  const fieldErrors = Array.isArray(body.errors)
    ? body.errors.flatMap((e) =>
        isRecord(e) &&
        typeof e.field === "string" &&
        typeof e.code === "string" &&
        typeof e.message === "string"
          ? [{ field: e.field, code: e.code, message: e.message }]
          : [],
      )
    : [];

  return {
    kind,
    status,
    code,
    requestId:
      nonEmptyString(body.requestId) ??
      nonEmptyString(headers.get("X-Request-Id")),
    fieldErrors,
    retryAfterSeconds:
      seconds(body.retryAfterSeconds) ?? seconds(headers.get("Retry-After")),
    allowedClients:
      code === "NOT_PERMITTED_FOR_CLIENT" && Array.isArray(body.allowedClients)
        ? body.allowedClients.filter((c): c is Api["Client"] =>
            CLIENTS.includes(c as Api["Client"]),
          )
        : null,
    ...(code === "CLASS_IN_USE" && Array.isArray(body.classesInUse)
      ? {
          classesInUse: body.classesInUse.filter(
            (r): r is Api["ClassSectionRef"] =>
              isRecord(r) && typeof r.classLevel === "string",
          ),
        }
      : {}),
    ...(code === "EXPORT_TOO_LARGE" && typeof body.matchCount === "number"
      ? { matchCount: body.matchCount }
      : {}),
    ...(code === "VERSION_CONFLICT" && isRecord(body.currentClasses)
      ? { currentClasses: body.currentClasses as Api["SchoolClassList"] }
      : {}),
  };
}

/** True for values built by toAppError. */
export function isAppError(value: unknown): value is AppError {
  return (
    isRecord(value) &&
    typeof value.kind === "string" &&
    "status" in value &&
    "requestId" in value &&
    Array.isArray(value.fieldErrors)
  );
}
